import { InfoOutlineIcon } from '@carboncopyco/icons';
import {
  ColorHex,
  TextCaption,
  Tooltip,
  TooltipMessage,
} from '@carboncopyco/ui';
import { ErrorMessage as HookFormErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import React, { useContext, useMemo, useRef } from 'react';
import type { ComponentProps } from 'react';

const FormFieldContext = React.createContext<{
  name: string;
}>({ name: '' });

export interface FormFieldProps {
  name: string;
  label?: string;
  tooltipContent?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export const FormField = ({
  name,
  label,
  tooltipContent,
  children,
  className,
}: FormFieldProps) => {
  const contextValue = useMemo(() => ({ name }), [name]);
  return (
    <FormFieldContext.Provider value={contextValue}>
      <div className={classNames('flex flex-col gap-1.5', className)}>
        {label && (
          <Label
            label={label}
            tooltipContent={tooltipContent}
          />
        )}
        {children}
      </div>
    </FormFieldContext.Provider>
  );
};

export const Label = ({
  label,
  tooltipContent,
}: {
  label?: string;
  tooltipContent?: React.ReactNode;
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <span className="flex gap-1 items-center">
      <TextCaption
        label={label}
        color="text-ds-natural-600"
      />
      {tooltipContent && (
        <>
          <span ref={anchorRef}>
            <InfoOutlineIcon
              color={ColorHex.natural300}
              size={16}
            />
          </span>
          <Tooltip
            anchorRef={anchorRef}
            placement="bottom-start"
            autoCalcArrowPosition
          >
            <TooltipMessage title={<span>{tooltipContent}</span>} />
          </Tooltip>
        </>
      )}
    </span>
  );
};

export type ErrorMessageProps = Pick<
  ComponentProps<typeof HookFormErrorMessage>,
  'errors'
>;

export const ErrorMessageText = ({ message }: { message: string }) => {
  return (
    <TextCaption
      label={message}
      color="text-ds-danger-400"
    />
  );
};

export const ErrorMessage = ({ errors }: ErrorMessageProps) => {
  const { name } = useContext(FormFieldContext);
  return (
    <HookFormErrorMessage
      errors={errors}
      name={name}
      render={({ message }) => <ErrorMessageText message={message} />}
    />
  );
};

// Counter for text fields that have a max character limit.
// It's displayed when the current number of characters is greater than 90% and 100% (as error message).
export const CharactersCounter = ({
  current,
  max,
  range = 0.9,
}: {
  current: number;
  max: number;
  range?: number;
}) => {
  const isInRange = current >= range * max;

  if (!isInRange) return null;

  return (
    <TextCaption
      color={current > max ? 'text-ds-danger-400' : 'text-ds-natural-300'}
      label={`${current}/${max}`}
    />
  );
};
